'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import {
  buildCanonicalHomeFeedUrl,
  buildHomeFeedUrl,
  type HomeFeedCountParam,
  normalizeHomeFeedCount,
} from '@/app/homeFeedQuery';

/** Provides canonical URL-backed counts for a page's active feed projections. */
export function useFeedUrlState(countParams: readonly HomeFeedCountParam[]) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const currentUrl = rawSearch ? `${pathname}?${rawSearch}` : pathname;
  const latestRawSearchRef = useRef(rawSearch);

  useEffect(() => {
    latestRawSearchRef.current = rawSearch;
  }, [currentUrl, rawSearch]);
  const counts = useMemo(
    () =>
      Object.fromEntries(
        countParams.map((countParam) => [
          countParam,
          normalizeHomeFeedCount(searchParams.get(countParam)),
        ])
      ) as Partial<Record<HomeFeedCountParam, number>>,
    [rawSearch, searchParams, countParams]
  );
  const canonicalUrl = useMemo(
    () => buildCanonicalHomeFeedUrl(pathname, rawSearch, countParams),
    [countParams, pathname, rawSearch]
  );

  useEffect(() => {
    if (canonicalUrl !== currentUrl) {
      latestRawSearchRef.current = canonicalUrl.split('?')[1] ?? '';
      router.replace(canonicalUrl, { scroll: false });
    }
  }, [canonicalUrl, currentUrl, router]);

  /** Replaces one count while carrying forward prior navigations and unrelated parameters. */
  const replaceFeedCount = useCallback(
    (nextCount: number, countParam: HomeFeedCountParam): void => {
      const nextUrl = buildHomeFeedUrl(pathname, latestRawSearchRef.current, nextCount, countParam);
      latestRawSearchRef.current = nextUrl.split('?')[1] ?? '';
      router.replace(nextUrl, { scroll: false });
    },
    [pathname, router]
  );

  return { counts, replaceFeedCount };
}
